import { useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import moment, { Moment } from 'moment';
import {
  useApolloContext,
  useLocationsContext,
  useUserContext,
} from '../../../contexts';
import {
  GetCurrentUserReservationsByDateWorkWeekQuery,
  GetCurrentUserReservationsByDateWorkWeekQueryVariables,
} from './__generated__/useCustomResourceReservations.generated';

const CUSTOM_RESOURCE_RESERVATIONS_QUERY = gql`
  query GetCurrentUserReservationsByDateWorkWeek(
    $organizationId: ID!
    $localDates: [IsoDateOnlyString!]!
    $localStartTime: IsoLocalTimeString!
    $duration: ISO8601_Duration!
  ) {
    customResourceReservationsForCurrentUserGroupedByDate(
      organizationId: $organizationId
      localDates: $localDates
      localStartTime: $localStartTime
      duration: $duration
    ) {
      id
      resourceReservationsByDate {
        id
        resourceInstanceId
        localDate
        localStartTime
        duration
        reservations {
          id
          date
          startTime
          duration
          reservee
          reservedBy
          resourceInstanceId
          isConfirmed
          confirmationTimestamp
          confirmationValidationState {
            errorCodes
            detailedErrors {
              failureCode
              meta
            }
          }
          isAssigned
          accountReservee {
            id
          }
          accountReservedBy {
            id
          }
          resource {
            originatingCustomResourceTemplateId
            customResourceInstanceId
            resourceGroupName
            name
            locationId
            floorId
            icon
            policyConfiguration {
              requireCheckIn
              localCheckInOnly
              advanceCheckInWindow {
                enabled
                duration
              }
            }
          }
        }
      }
    }
  }
`;

export function useCustomResourceReservations(weekScheduleDates: Moment[]) {
  const { currentUser } = useUserContext();
  const { selectedLocation } = useLocationsContext();
  const { tenantId } = useApolloContext();

  const localDates = useMemo(() => {
    return weekScheduleDates.map((date) => moment(date).format('YYYY-MM-DD'));
  }, [weekScheduleDates]);

  return useQuery<
    GetCurrentUserReservationsByDateWorkWeekQuery,
    GetCurrentUserReservationsByDateWorkWeekQueryVariables
  >(CUSTOM_RESOURCE_RESERVATIONS_QUERY, {
    skip: !currentUser?.id || !tenantId || !selectedLocation?.id,
    variables: {
      organizationId: tenantId as string,
      localDates,
      localStartTime: '00:00:00',
      duration: 'PT24H',
    },
    fetchPolicy: 'no-cache',
  });
}
